import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaArchive } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { useModal } from 'store/modal';
import Form from 'components/form/Form';
import Button from 'components/Button';
import FormGroup from 'components/form/FormGroup';
import TooltipWrapper from 'components/TooltipWrapper';
import { archiveDoc } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  children?: any;
  id: string;
  buttonText: string;
  disabled: boolean;
  docHolderStatus: string;
  entityName: string;
  invalidate: () => void;
  handlePreview: (id: string) => void;
  className?: string;
}

interface IPropsReasonForm {
  handleArchive?: (id: string, data?: { [key: string]: string }) => void;
  docId: string;
  entityName: string;
}

interface IInputs {
  reason: string;
}

// Archive reason form
const ReasonForm: FC<IPropsReasonForm> = ({ handleArchive, entityName, docId }: IPropsReasonForm) => {
  const methods = useForm<IInputs>();
  const { hideModal } = useModal();
  const { t } = useTranslation();
  const showInput = entityName !== 'documents-received';
  return (
    <>
      {showInput && <p className="text-center">{t('documents.table.confirmArchiveReasonLabel')}</p>}
      <Form
        methods={methods}
        onSubmit={(data: IInputs) => {
          // Prevent submitting empty string
          if (showInput && data.reason.trim() !== '') {
            handleArchive!(docId, { reason: data.reason });
            return;
          }
          handleArchive!(docId);
        }}
        btnLabel={t('documents.table.confirmArchiveDocLabel')}
        primaryActionBtnType="danger"
        onCancel={() => hideModal()}
      >
        {showInput && (
          <FormGroup
            controlId="formGroupReason"
            as="textarea"
            name="reason"
            isRequired
          />
        )}
      </Form>
    </>
  );
};

const ArchiveDoc: FC<IProps> = ({
  id,
  entityName,
  buttonText,
  disabled,
  docHolderStatus,
  invalidate,
  handlePreview,
  className,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  // Archive document
  const handleArchive = (docId: string, data?: { [key: string]: string }) =>
    API.post(`document-holders/${docId}/archive`, {
      reason: data?.reason || null,
    })
      .then(res => {
        const { message } = res.data;
        showModal({
          type: 'success',
          content: t('documents.table.archivedDocSuccessMsg'),
          onSuccess: () => {
            if (message?.type === 'archive') {
              archiveDoc(docId.toString(), message.body, showModal, () => invalidateQueries([], invalidate), t);
            }
          },
        });
        invalidateQueries([], invalidate);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );

  // Confirm archive
  const confirmArchive = (docId: string, status: string) => {
    //  If the user is released from work, then show form with reason for archivation field
    if (status === 'released') {
      return showModal({
        type: 'form',
        heading: t('documents.table.confirmDocHeading'),
        content: (
          <>
            <p>{t('documents.table.confirmArchiveDocMsg')}</p>
            <Button
              type="button"
              btnType="primary"
              outlined
              disabled={isLoading}
              onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
            >
              {!isLoading && t('documents.table.previewDoc')}
              {isLoading && <Spinner size={14} />}
            </Button>
            <hr />
            <ReasonForm
              entityName={entityName}
              handleArchive={handleArchive}
              docId={docId}
            />
          </>
        ),
      });
    }

    showModal({
      type: 'confirm',
      heading: t('documents.table.confirmArchiveDocHeading'),
      content: t('documents.table.confirmArchiveDocMsg'),
      onConfirm: () => handleArchive(docId),
      confirmLabel: t('documents.table.confirmArchiveDocLabel'),
    });
  };

  const containerClass = className || 'ml-3';

  return (
    <div className={`d-inline-block ${containerClass}`}>
      <TooltipWrapper
        id="tooltip-archive"
        overlay={buttonText}
      >
        <Button
          type="button"
          btnType="icon"
          disabled={disabled}
          onClick={e => {
            confirmArchive(id, docHolderStatus);
            e.stopPropagation();
          }}
        >
          <FaArchive size={18} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default ArchiveDoc;
